import React from "react";
import { LEVELS, levelsOf } from "@/lib/levels";

/**
 * Wave C · placeholder level icons.
 *
 * Vania 7 Oct: "Icons stack (an insider who is also a curator shows both;
 * founding crew shows its own icon instead of the plain insider one).
 * Placeholder icons for now; designs follow."
 *
 * We render tiny inline SVG marks right next to a person's name. The marks
 * are intentionally minimal glyphs so the final designs can drop in without
 * breaking layouts. Each icon has data-testid `level-icon-{level}` so the
 * final design pass can swap them without touching call-sites.
 */
const SIZE = 12;
const YELLOW = "#FFD60A";
const INK = "var(--ww-text)";
const MUTED = "var(--ww-text-muted)";

function GuestMark() {
  return (
    <svg width={SIZE} height={SIZE} viewBox="0 0 12 12" aria-hidden
         data-testid="level-icon-guest">
      <circle cx="6" cy="6" r="5" fill="none" stroke={MUTED} strokeDasharray="2 2" strokeWidth="1.2" />
    </svg>
  );
}
function InMark() {
  return (
    <svg width={SIZE} height={SIZE} viewBox="0 0 12 12" aria-hidden
         data-testid="level-icon-in">
      <circle cx="6" cy="6" r="4.5" fill="none" stroke={INK} strokeWidth="1.4" />
    </svg>
  );
}
function InsiderMark() {
  return (
    <svg width={SIZE} height={SIZE} viewBox="0 0 12 12" aria-hidden
         data-testid="level-icon-insider">
      <circle cx="6" cy="6" r="4.5" fill={INK} />
      <circle cx="6" cy="6" r="2" fill={YELLOW} />
    </svg>
  );
}
function CuratorMark() {
  return (
    <svg width={SIZE} height={SIZE} viewBox="0 0 12 12" aria-hidden
         data-testid="level-icon-curator">
      <path d="M6 1 L7.4 4.4 L11 4.6 L8.2 7 L9.1 10.5 L6 8.6 L2.9 10.5 L3.8 7 L1 4.6 L4.6 4.4 Z"
            fill="none" stroke={INK} strokeWidth="1.1" strokeLinejoin="round" />
    </svg>
  );
}
function FoundingCrewMark() {
  return (
    <svg width={SIZE} height={SIZE} viewBox="0 0 12 12" aria-hidden
         data-testid="level-icon-founding-crew">
      <path d="M6 1 L7.4 4.4 L11 4.6 L8.2 7 L9.1 10.5 L6 8.6 L2.9 10.5 L3.8 7 L1 4.6 L4.6 4.4 Z"
            fill={YELLOW} stroke={INK} strokeWidth="0.9" strokeLinejoin="round" />
    </svg>
  );
}

const MARKS = {
  [LEVELS.GUEST]: GuestMark,
  [LEVELS.IN]: InMark,
  [LEVELS.INSIDER]: InsiderMark,
  [LEVELS.CURATOR]: CuratorMark,
  [LEVELS.FOUNDING_CREW]: FoundingCrewMark,
};

/**
 * Render a stacked row of icons for one person. Pass `user` (anything with
 * membership_state and is_curator) and we work out the levels. Set
 * `hideBase` to skip the "in" baseline icon when the row would otherwise be
 * noise (e.g. inside a squad members list where "in" is the common case).
 */
export default function LevelIcon({ user, hideBase = false, gap = 3, className = "" }) {
  const levels = levelsOf(user);
  const shown = hideBase ? levels.filter((l) => l !== LEVELS.IN && l !== LEVELS.GUEST) : levels;
  if (shown.length === 0) return null;
  return (
    <span className={className} data-testid="level-icon-row"
          style={{ display: "inline-flex", alignItems: "center", gap, verticalAlign: "-1px",
                   lineHeight: 0 }}>
      {shown.map((l) => {
        const Mark = MARKS[l] || InMark;
        return <Mark key={l} />;
      })}
    </span>
  );
}
